import React, { useMemo } from 'react';
import { FiEdit2, FiTrash2, FiCheck, FiX } from 'react-icons/fi';
import { formatCurrency } from '../../../utils/formatters';
import { useTranslation } from 'react-i18next';
import CalculationItemManager from './CalculationItemManager';

const CalculationItemRow = ({ 
  item, 
  calculationId, 
  onUpdateSuccess,
  onDelete,
  readOnly = false
}) => {
  const { t } = useTranslation();
  
  // Calculate total
  const itemTotal = useMemo(() => {
    const quantity = parseFloat(item.quantity) || 0;
    const sellPrice = parseFloat(item.sellPrice) || 0;
    return quantity * sellPrice;
  }, [item.quantity, item.sellPrice]);
  
  // Format date if available
  const formattedDate = useMemo(() => {
    if (!item.updatedAt) return null;
    return new Date(item.updatedAt).toLocaleDateString();
  }, [item.updatedAt]);

  return (
    <tr className="border-t border-gray-200 hover:bg-gray-50">
      {/* Product Code */}
      <td className="px-6 py-4 whitespace-nowrap">
        <div className="text-sm font-medium text-gray-900">
          {item.productCode || '-'}
        </div>
      </td>
      
      {/* Product Description */}
      <td className="px-6 py-4">
        <div className="text-sm text-gray-900 max-w-xs break-words">
          {item.productDescription || '-'}
        </div>
        {formattedDate && (
          <div className="text-xs text-gray-500">
            {t('Updated')}: {formattedDate}
          </div>
        )}
      </td>
      
      {/* Quantity */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm text-gray-900">
          {parseFloat(item.quantity || 0).toLocaleString(undefined, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 3
          })}
        </div>
      </td>
      
      {/* List Price */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm text-gray-900">
          {formatCurrency(item.listPrice || 0)}
        </div>
      </td>
      
      {/* Buy Price */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm text-gray-900">
          {formatCurrency(item.buyPrice || 0)}
        </div>
      </td>
      
      {/* Stock Price */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm text-gray-900">
          {formatCurrency(item.stockPrice || 0)}
        </div>
      </td>
      
      {/* Sell Price */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm font-medium text-gray-900">
          {formatCurrency(item.sellPrice || 0)}
        </div>
      </td>
      
      {/* Customs Value */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm text-gray-900">
          {item.customsValue || 0}
        </div>
      </td>
      
      {/* Customs Availability */}
      <td className="px-6 py-4 whitespace-nowrap text-center">
        <div className="flex justify-center">
          {item.customsAvailability ? (
            <FiCheck className="h-5 w-5 text-green-500" />
          ) : (
            <FiX className="h-5 w-5 text-red-500" />
          )}
        </div>
      </td>
      
      {/* Total */}
      <td className="px-6 py-4 whitespace-nowrap text-right">
        <div className="text-sm font-medium text-gray-900">
          {formatCurrency(itemTotal)}
        </div>
      </td>
      
      {/* Actions */}
      {!readOnly && (
        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
          <div className="flex justify-end space-x-1">
            <CalculationItemManager
              calculationId={calculationId}
              item={item}
              onSuccess={onUpdateSuccess}
              buttonVariant="ghost"
              iconOnly={true}
            />
            
            <button
              type="button"
              className="p-1.5 text-red-600 hover:text-red-900 rounded-full hover:bg-red-50"
              onClick={(e) => {
                e.stopPropagation();
                if (window.confirm(t('Are you sure you want to delete this item?'))) {
                  onDelete(item.id);
                }
              }}
              title={t('Delete item')}
            >
              <FiTrash2 className="h-4 w-4" />
            </button>
          </div>
        </td>
      )}
    </tr>
  );
};

export default CalculationItemRow;
